// Item store on DynamoDB.
//
// Table layout (set up by infra/lib/data-stack.ts):
//   PK = userId (S)  — either the caller's Cognito Identity ID, or the
//                      literal "shared" for items in the shared workspace
//   SK = itemId (S)  — client-generated unique id
//   attributes: content (S), createdAt (S, ISO-8601)
//
// IAM policy on the auth role grants `dynamodb:LeadingKeys` access to:
//   • the caller's own Cognito Identity ID (private per-user data), and
//   • the literal "shared" (any signed-in user may read or write).
// All other partition keys are inaccessible at the AWS layer; the client
// physically cannot query another user's private rows.
//
// Pass `SHARED_PARTITION_KEY` as `userId` to any of these functions to
// operate on the shared workspace. The string is duplicated in
// infra/lib/auth-stack.ts — keep them in sync if renaming.
//
// The top-level exports operate on the main user-data table
// (`config.dynamoTable`). Other tables with the same key schema get their own
// bound store via `tableStore(tableName)` — e.g. the finance table (see
// model/finance/store.js), which is per-user ONLY (its IAM grant has no
// `shared` partition, so SHARED_PARTITION_KEY is denied there).

import {
  PutCommand,
  QueryCommand,
  DeleteCommand,
  GetCommand,
  UpdateCommand,
} from '@aws-sdk/lib-dynamodb';
import { config } from '../core/config.js';

/** Partition key value for the shared (all-signed-in-users) workspace. */
export const SHARED_PARTITION_KEY = 'shared';

function randomId() {
  // Browser-native UUID is available in every supported target (es2020+).
  return crypto.randomUUID();
}

/**
 * Build the item-store API bound to one DynamoDB table (PK `userId`, SK
 * `itemId`). Every function takes the DocumentClient `doc` first, exactly like
 * the top-level exports below.
 */
export function tableStore(tableName) {
  if (!tableName) throw new Error('tableStore: a table name is required');

  /**
   * Create a new item with a generated itemId (itemIdPrefix + random uuid).
   * Returns the full stored item, including the generated `itemId` and
   * `createdAt`, so callers can reference the new row.
   */
  async function addItem(doc, userId, itemIdPrefix, item, itemId = null) {
    const dbItem = {
      userId: userId,
      itemId: itemId ?? itemIdPrefix + randomId(),
      createdAt: new Date().toISOString(),
    };
    Object.assign(dbItem, item);
    await doc.send(new PutCommand({ TableName: tableName, Item: dbItem }));
    return dbItem;
  }

  // Reads are STRONGLY consistent: the app often reads straight after its own
  // writes (a deferred Eat's read-modify-write right after the Prepare that
  // created the row; stock reloads right after flushing pending writes), and an
  // eventually-consistent read can miss a just-landed write (→ phantom stock).
  // Base-table only — a GSI query can't be consistent (none are used here).
  async function getItem(doc, userId, itemId, mappingFunc = null) {
    const result = await doc.send(
      new GetCommand({ TableName: tableName, Key: { userId, itemId }, ConsistentRead: true }),
    );
    if (!result.Item) return null;
    return mappingFunc ? mappingFunc(result.Item) : result.Item;
  }

  async function listItems(doc, userId, itemIdPrefix = '', mappingFunc = null) {
    const result = await doc.send(
      new QueryCommand({
        TableName: tableName,
        KeyConditionExpression: 'userId = :pk AND begins_with(itemId, :sk)',
        ExpressionAttributeValues: {
          ':pk': userId,
          ':sk': itemIdPrefix,
        },
        ConsistentRead: true,
      }),
    );
    const items = result.Items ?? [];
    return mappingFunc ? items.map(mappingFunc) : items;
  }

  /**
   * Partially update an existing item: set each attribute in `attrs` without
   * touching other attributes. Attribute names are passed through
   * ExpressionAttributeNames so reserved words (e.g. "name") are always safe.
   * Creates the item if it doesn't exist (DynamoDB UpdateItem upsert semantics).
   */
  async function updateItem(doc, userId, itemId, attrs) {
    const names = {};
    const values = {};
    const sets = [];
    let i = 0;
    for (const [key, value] of Object.entries(attrs)) {
      const nameToken = `#k${i}`;
      const valueToken = `:v${i}`;
      names[nameToken] = key;
      values[valueToken] = value;
      sets.push(`${nameToken} = ${valueToken}`);
      i += 1;
    }
    if (sets.length === 0) return;
    await doc.send(
      new UpdateCommand({
        TableName: tableName,
        Key: { userId, itemId },
        UpdateExpression: `SET ${sets.join(', ')}`,
        ExpressionAttributeNames: names,
        ExpressionAttributeValues: values,
      }),
    );
  }

  async function deleteItem(doc, userId, itemId) {
    await doc.send(new DeleteCommand({ TableName: tableName, Key: { userId, itemId } }));
  }

  /**
   * Remove one or more attributes from an existing item (a no-op if the item or
   * the attributes don't exist). Complements `updateItem`, which can only SET.
   * Names go through ExpressionAttributeNames so reserved words are always safe.
   */
  async function removeItemAttrs(doc, userId, itemId, attrNames) {
    const names = {};
    const removes = [];
    let i = 0;
    for (const key of attrNames) {
      const token = `#k${i}`;
      names[token] = key;
      removes.push(token);
      i += 1;
    }
    if (removes.length === 0) return;
    await doc.send(
      new UpdateCommand({
        TableName: tableName,
        Key: { userId, itemId },
        UpdateExpression: `REMOVE ${removes.join(', ')}`,
        ExpressionAttributeNames: names,
      }),
    );
  }

  return { tableName, addItem, getItem, listItems, updateItem, deleteItem, removeItemAttrs };
}

// The main user-data table — the default store every existing model uses.
const userDataStore = tableStore(config.dynamoTable);

export const addItem = userDataStore.addItem;
export const getItem = userDataStore.getItem;
export const listItems = userDataStore.listItems;
export const updateItem = userDataStore.updateItem;
export const deleteItem = userDataStore.deleteItem;
export const removeItemAttrs = userDataStore.removeItemAttrs;
